import { createEffect } from "solid-js";
import c from "class-c";

import t from "@repo/localization/string.macro";
import createAsync from "@repo/signals/createAsync";

import styles from "./PasswordStrength.module.scss";

interface ScoreState {
  name: string;
  color: string;
}

const scoreStates = () =>
  [
    {
      name: t`Very Weak`,
      color: "password-very-weak",
    },
    {
      name: t`Weak`,
      color: "password-weak",
    },
    {
      name: t`Fair`,
      color: "password-fair",
    },
    {
      name: t`Strong`,
      color: "password-strong",
    },
    {
      name: t`Very Strong`,
      color: "password-very-strong",
    },
  ] satisfies ScoreState[];

declare namespace PasswordStrength {
  type Props = {
    password: string;
    onScoreChange?: (score: Score) => void;
  };
  type Score = ReturnType<typeof import("zxcvbn")>["score"];
}

function PasswordStrength({
  password,
  onScoreChange,
}: D<PasswordStrength.Props>) {
  const score = createAsync(() => {
    password;
    return import("zxcvbn").then(
      (m) => m.default(password.slice(0, 100)).score
    );
  }, 0);

  createEffect(() => {
    onScoreChange?.(score());
  });

  return (
    <div>
      <div class={styles["bar-outer"]}>
        <div
          class={styles["bar-inner"]}
          style={{
            width: `${(score() + 1) * 20}%`,
            "background-color": `var(--${scoreStates()[score()]?.color})`,
          }}
        >
          <div class={c`${styles["bar-inner-text"]} text:mini++`}>
            {scoreStates()[score()]?.name}
          </div>
        </div>
      </div>
      {(
        [
          [() => password.length >= 8, t`Must be at least 8 characters long`],
          [() => score() >= 1, t`Must not be very weak`],
        ] as const
      ).map(([condition, message]) => (
        <div
          class={c`mt-1 ${condition() ? "text:regular+" : "color:moderate-weak"}`}
        >
          <span class={c`${condition() && "color:primary"}`}></span> {message}
        </div>
      ))}
    </div>
  );
}

export default PasswordStrength;
